import React from "react";
import { useNavigate } from "react-router-dom";
import { FiArrowRight, FiUser } from "react-icons/fi";
import PrimaryButton from "../components/PrimaryButton";
import MobilePublicSeo from "../seo/MobilePublicSeo";
import welcomeBackground from "../../assets/images/jetblack-mobile-welcome.png";

const styles = {
  screen: {
    position: "relative",
    isolation: "isolate",
    width: "100%",
    minHeight: "100dvh",
    overflow: "hidden",
    backgroundImage: `url(${welcomeBackground})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  },
  content: {
    position: "relative",
    width: "100%",
    maxWidth: "480px",
    minHeight: "100dvh",
    margin: "0 auto",
    color: "#070b14",
    fontFamily: "Arial, Helvetica, sans-serif",
  },
  brand: {
    position: "absolute",
    top: "clamp(3.4rem, 8.6dvh, 4.85rem)",
    left: "50%",
    width: "100%",
    transform: "translateX(-50%)",
    textAlign: "center",
  },
  monogram: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "clamp(3.7rem, 14vw, 4.6rem)",
    marginBottom: "clamp(0.4rem, 1.5dvh, 0.75rem)",
    color: "#bd8732",
    fontFamily: "Bodoni MT, Didot, Georgia, serif",
    fontSize: "clamp(3.85rem, 15.7vw, 5rem)",
    fontWeight: 400,
    letterSpacing: "-0.18em",
    lineHeight: 0.7,
  },
  monogramLetter: {
    display: "inline-block",
  },
  monogramSecondLetter: {
    marginLeft: "-0.13em",
  },
  wordmark: {
    margin: 0,
    color: "#050a15",
    fontFamily: "Bodoni MT, Didot, Georgia, serif",
    fontSize: "clamp(1.75rem, 8.7vw, 2.3rem)",
    fontWeight: 400,
    letterSpacing: "0.075em",
    lineHeight: 1,
    whiteSpace: "nowrap",
  },
  limousineRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "clamp(0.4rem, 1.8vw, 0.6rem)",
    marginTop: "clamp(0.45rem, 1.25dvh, 0.7rem)",
  },
  goldLine: {
    width: "clamp(1.1rem, 5.2vw, 1.55rem)",
    height: "1px",
    backgroundColor: "#c18b35",
  },
  limousine: {
    color: "#bd8732",
    fontSize: "clamp(0.9rem, 4.45vw, 1.15rem)",
    fontWeight: 400,
    letterSpacing: "0.29em",
    lineHeight: 1,
    textIndent: "0.29em",
  },
  tagline: {
    margin: "clamp(0.75rem, 2.25dvh, 1.1rem) 0 0",
    color: "#080b13",
    fontSize: "clamp(0.94rem, 4vw, 1.1rem)",
    fontWeight: 400,
    letterSpacing: "0.01em",
    lineHeight: 1.2,
  },
  actions: {
    position: "absolute",
    right: "clamp(1.25rem, 8vw, 2rem)",
    bottom: "clamp(2.75rem, 8.8dvh, 4.6rem)",
    left: "clamp(1.25rem, 8vw, 2rem)",
    display: "flex",
    flexDirection: "column",
    gap: "clamp(0.7rem, 1.65dvh, 0.85rem)",
  },
  button: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    minHeight: "clamp(2.8rem, 10.8vw, 3.25rem)",
    padding: "0.55rem 1.1rem",
    borderRadius: "clamp(0.65rem, 2.7vw, 0.85rem)",
    fontFamily: "Arial, Helvetica, sans-serif",
    fontSize: "clamp(0.95rem, 4.4vw, 1.15rem)",
    fontWeight: 500,
    letterSpacing: "0.015em",
    lineHeight: 1,
  },
  continueButton: {
    position: "relative",
    border: "1px solid #070707",
    backgroundColor: "rgba(5, 5, 5, 0.98)",
    color: "#ffffff",
  },
  newHereButton: {
    gap: "clamp(0.8rem, 5.5vw, 1.55rem)",
    border: "1px solid #c48d37",
    backgroundColor: "rgba(255, 255, 255, 0.24)",
    color: "#080b13",
  },
  arrow: {
    position: "absolute",
    right: "clamp(1rem, 4.5vw, 1.35rem)",
    color: "#c38c35",
    fontSize: "clamp(1.35rem, 6.2vw, 1.7rem)",
    strokeWidth: 1.5,
  },
  userIcon: {
    flex: "0 0 auto",
    fontSize: "clamp(1.1rem, 5.2vw, 1.42rem)",
    strokeWidth: 1.55,
  },
  signIn: {
    margin: "clamp(0.35rem, 1dvh, 0.6rem) 0 0",
    color: "#11131a",
    fontSize: "clamp(0.82rem, 3.75vw, 0.98rem)",
    fontWeight: 400,
    letterSpacing: "0.005em",
    lineHeight: 1.25,
    textAlign: "center",
  },
  signInLink: {
    padding: 0,
    border: 0,
    background: "transparent",
    color: "#bd8732",
    font: "inherit",
    cursor: "pointer",
  },
};

function WelcomeScreen({ disableSeo = false, headingTag = "h1" }) {
  const navigate = useNavigate();
  const BrandHeading = headingTag;

  return (
    <main data-mobile-screen="welcome" style={styles.screen}>
      {!disableSeo && <MobilePublicSeo />}
      <div style={styles.content}>
        <section style={styles.brand} aria-label="Jet Black Limousine">
          <div style={styles.monogram} aria-hidden="true">
            <span style={styles.monogramLetter}>J</span>
            <span style={{ ...styles.monogramLetter, ...styles.monogramSecondLetter }}>B</span>
          </div>
          <BrandHeading style={styles.wordmark}>JET BLACK</BrandHeading>
          <div style={styles.limousineRow} aria-label="Limousine">
            <span style={styles.goldLine} />
            <span style={styles.limousine}>LIMOUSINE</span>
            <span style={styles.goldLine} />
          </div>
          <p style={styles.tagline}>On Time. Safe. Reliable.</p>
        </section>

        <section style={styles.actions} aria-label="Welcome options">
          <PrimaryButton
            style={{ ...styles.button, ...styles.continueButton }}
            onClick={() => navigate("/app/home")}
          >
            CONTINUE TO BOOKING
            <FiArrowRight style={styles.arrow} aria-hidden="true" />
          </PrimaryButton>
          <PrimaryButton style={{ ...styles.button, ...styles.newHereButton }} onClick={() => navigate("/app/auth/register")}>
            <FiUser style={styles.userIcon} aria-hidden="true" />
            I&apos;M NEW HERE
          </PrimaryButton>
          <p style={styles.signIn}>
            Already have an account? <button type="button" style={styles.signInLink} onClick={() => navigate("/app/auth/login")}>Sign In</button>
          </p>
        </section>
      </div>
    </main>
  );
}

export default WelcomeScreen;
